import React from 'react';
import { cn } from './cn';

/**
 * Minimal inline markup for the copy stored in `translations.ts`.
 *
 * Those strings are rendered as text nodes, so HTML written inside them shows up literally on
 * screen. The alternative — `dangerouslySetInnerHTML` — puts an injection footgun in a file that
 * is edited constantly for wording, so the handful of marks we actually need are parsed into
 * real elements instead:
 *
 *   **îngroșat**   __subliniat__   *înclinat*
 *
 * A blank line starts a new paragraph; a single newline is a line break.
 */

const INLINE_PATTERN = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*)/g;

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  return text
    .split(INLINE_PATTERN)
    .filter((part) => part !== '')
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;

      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={key} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (part.startsWith('__') && part.endsWith('__')) {
        return (
          <u key={key} className="underline underline-offset-2">
            {part.slice(2, -2)}
          </u>
        );
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }

      // Single newlines stay meaningful without forcing `whitespace-pre-wrap` on the container,
      // which would also preserve the indentation of the source string.
      const lines = part.split('\n');
      return (
        <React.Fragment key={key}>
          {lines.map((line, lineIndex) => (
            <React.Fragment key={`${key}-l${lineIndex}`}>
              {lineIndex > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    });
}

export interface RichTextProps {
  text: string;
  /** Applied to the wrapper, so paragraphs inherit size and colour from one place. */
  className?: string;
}

const RichText: React.FC<RichTextProps> = ({ text, className }) => {
  const paragraphs = text.split(/\n{2,}/).filter((paragraph) => paragraph.trim() !== '');

  return (
    <div className={cn('space-y-2', className)}>
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{renderInline(paragraph, String(index))}</p>
      ))}
    </div>
  );
};

export default RichText;
